import { ApiError, handleApiError } from 'utils/api';
import { env } from '../../constants';
import {
  GuestPrefill,
  GuestRedeemResponse,
  GuestSaveRequest
} from './api-types';

const GUEST_CV_URL = `${env.VITE_API_BASE_URL}/guest/curriculumVitaes`;

/**
 * Map a failed guest response to a typed {@link ApiError} the UI renders via
 * `getErrorContent`. Unlike the authenticated client, the guest flow never reloads on
 * 401 (there is no Auth0 session to recover) — an expired/invalid session surfaces as a
 * screen prompting a new link.
 *  - 401/403 → session expired or invalid (re-redeem / request a new link)
 *  - 404     → feature unavailable (flag off) or unknown route
 *  - 400     → structured server error ({ code, values }, e.g. error.grant.expired,
 *              error.grant.already-submitted, error.cv.incomplete)
 */
const throwGuestError = async (response: Response): Promise<never> => {
  if (response.status === 401 || response.status === 403) {
    throw new ApiError({ code: 'error.guest.session-expired', values: {} });
  }
  if (response.status === 404) {
    throw new ApiError({ code: 'error.guest.unavailable', values: {} });
  }
  if (response.status === 400) {
    await handleApiError(response);
  }
  throw new ApiError({ code: 'error.guest.unknown', values: {} });
};

export const redeemGuestCv = async (
  linkToken: string
): Promise<GuestRedeemResponse> => {
  const response = await fetch(`${GUEST_CV_URL}/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: linkToken })
  });

  if (!response.ok) {
    await throwGuestError(response);
  }

  const json = (await response.json()) as { data: GuestRedeemResponse };
  return json.data;
};

export const saveGuestCv = async (
  sessionToken: string,
  payload: GuestSaveRequest,
  signal?: AbortSignal
): Promise<void> => {
  const response = await fetch(GUEST_CV_URL, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  if (!response.ok) {
    await throwGuestError(response);
  }
};

/**
 * Best-effort personal-detail suggestions for prefilling the form. Deliberately never throws:
 * any non-OK (404 when the flag is off, 401, 5xx) or network error resolves to `null` so the
 * form simply opens blank — prefill is a convenience, not a gate.
 */
export const fetchGuestCvPrefill = async (
  sessionToken: string
): Promise<GuestPrefill | null> => {
  try {
    const response = await fetch(`${GUEST_CV_URL}/prefill`, {
      headers: { Authorization: `Bearer ${sessionToken}` }
    });
    if (!response.ok) {
      return null;
    }
    const json = (await response.json()) as { data: GuestPrefill | null };
    return json.data ?? null;
  } catch {
    return null;
  }
};

export const submitGuestCv = async (sessionToken: string): Promise<void> => {
  const response = await fetch(`${GUEST_CV_URL}/submit`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${sessionToken}` }
  });

  if (!response.ok) {
    await throwGuestError(response);
  }
};
